import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { getNotifyPrefs, saveNotifyPrefs } from "@/lib/push";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail } from "lucide-react";

const ROWS = [
  { key: "assigned", label: "Task assignments", hint: "When a task is assigned to you" },
  { key: "status", label: "Status changes", hint: "When a task you're on changes status" },
  { key: "comment", label: "Comments", hint: "New comments on your tasks" },
];

export default function NotificationPrefs() {
  const [state, setState] = useState(null); // {preferences, email}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getNotifyPrefs()
      .then(setState)
      .catch(() =>
        setState({
          preferences: { assigned: true, status: true, comment: true },
          email: { assigned: true, status: true, comment: true },
        })
      );
  }, []);

  const toggle = async (channel, key) => {
    if (saving || !state) return;
    const prev = state;
    const next = { ...state, [channel]: { ...state[channel], [key]: !state[channel][key] } };
    // In-app off also silences email for that event (email requires in-app on).
    if (channel === "preferences" && next.preferences[key] === false) {
      next.email = { ...next.email, [key]: false };
    }
    setState(next);
    setSaving(true);
    try {
      await saveNotifyPrefs(next);
    } catch (e) {
      setState(prev);
      toast.error("Could not save preference");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return <div className="px-4 py-4 text-xs text-zinc-400">Loading preferences…</div>;

  return (
    <div className="px-4 py-3" data-testid="notification-prefs">
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Alert me about</p>
        <div className="flex items-center gap-4 pr-0.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500" title="In-app + desktop">
            <Bell className="h-3 w-3" /> App
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500" title="Email to your inbox">
            <Mail className="h-3 w-3" /> Email
          </span>
        </div>
      </div>
      <div className="space-y-3">
        {ROWS.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium leading-tight">{r.label}</p>
              <p className="text-[11px] text-zinc-500 leading-tight">{r.hint}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <Switch
                checked={!!state.preferences[r.key]}
                onCheckedChange={() => toggle("preferences", r.key)}
                disabled={saving}
                data-testid={`notify-pref-${r.key}`}
                aria-label={`${r.label} in-app`}
              />
              <Switch
                checked={!!state.email[r.key]}
                onCheckedChange={() => toggle("email", r.key)}
                disabled={saving || !state.preferences[r.key]}
                data-testid={`notify-email-${r.key}`}
                aria-label={`${r.label} email`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
